/**
 * Four user-facing stages of the AIRA draft pipeline. These are a coarse,
 * UX-friendly grouping over the 15 backend graph nodes. Both add-ins render
 * the same stepper and the same `derivePlanCard` view-model so the Gmail
 * compose card and the Outlook task pane never visually drift.
 *
 * Mapping rationale lives in `phaseToStage` below.
 */
import { PHASE_LABELS } from '../progress.js';
export const STAGES = ['classify', 'retrieve', 'draft', 'review'];
export const STAGE_LABELS = {
    classify: 'Classify',
    retrieve: 'Retrieve',
    draft: 'Draft',
    review: 'Review',
};
export const STAGE_DESCRIPTIONS = {
    classify: 'Understanding what the customer is asking for',
    retrieve: 'Looking up merchant knowledge & order details',
    draft: 'Composing the reply',
    review: 'Checking the draft against policy',
};
/**
 * Maps a backend `current_phase` to one of the four user-facing stages.
 * Returns `null` for warm-up phases that should NOT advance the stepper.
 */
export function phaseToStage(currentPhase) {
    if (!currentPhase)
        return null;
    switch (currentPhase) {
        case 'classifyIntent':
            return 'classify';
        case 'loadRouting':
        case 'resolveDimensions':
        case 'searchQueryEnrichment':
        case 'retrieveHybrid':
        case 'reranker':
        case 'executionAgent':
        case 'aggregateContext':
            return 'retrieve';
        case 'generateDraft':
            return 'draft';
        case 'guardrail':
        case 'persistRun':
            return 'review';
        default:
            // queued, loadTenantConfig, piiAnonymization, or anything new the backend adds.
            return null;
    }
}
/**
 * Builds an ordered list of stages with per-stage status, given the current
 * backend phase and the overall run status. Stages before the active one are
 * `done`; the active stage is `active`; later stages are `pending`. When the
 * run has completed all four are `done`; when failed, the failing stage is
 * `failed` and later stages are `pending`.
 */
export function deriveStageStatuses(currentPhase, runStatus) {
    const active = phaseToStage(currentPhase);
    const activeIndex = active ? STAGES.indexOf(active) : -1;
    return STAGES.map((stage, index) => {
        let status;
        if (runStatus === 'completed') {
            status = 'done';
        }
        else if (runStatus === 'failed' && index === activeIndex) {
            status = 'failed';
        }
        else if (activeIndex < 0) {
            status = 'pending';
        }
        else if (index < activeIndex) {
            status = 'done';
        }
        else if (index === activeIndex) {
            status = 'active';
        }
        else {
            status = 'pending';
        }
        return {
            stage,
            label: STAGE_LABELS[stage],
            description: STAGE_DESCRIPTIONS[stage],
            status,
        };
    });
}
/**
 * Maps a 0..1 confidence to a discrete badge. `unknown` is reserved for the
 * case where the backend did not emit a confidence — we never invent one.
 *
 * Thresholds mirror what Outlook had hardcoded in ragBackend.ts (0.6 / 0.8).
 */
export function confidenceToBadge(confidence) {
    if (typeof confidence !== 'number' || Number.isNaN(confidence))
        return 'unknown';
    if (confidence >= 0.8)
        return 'high';
    if (confidence >= 0.6)
        return 'medium';
    return 'low';
}
/**
 * Picks a short, user-facing title for the current state. We avoid surfacing
 * the raw node name (e.g. "retrieveHybrid") — that's internal vocabulary.
 */
function titleFor(input, active) {
    if (input.status === 'completed' || input.draftReady)
        return 'Reply draft ready';
    if (input.status === 'failed')
        return 'Draft failed';
    if (!active)
        return 'Warming up…';
    return `${STAGE_LABELS[active]}…`;
}
/**
 * Pulls the most recent non-trivial phase label as a subtitle hint. Falls
 * back to the stage description so the bullets always have something to show
 * even on the very first poll before any events have been recorded.
 */
function subtitleFor(input, active) {
    if (input.status === 'completed' || input.draftReady) {
        return input.intentLabel ? `Intent: ${input.intentLabel}` : 'Open the reply to review.';
    }
    if (input.status === 'failed')
        return 'Something went wrong while drafting.';
    if (input.currentPhase && PHASE_LABELS[input.currentPhase]) {
        return PHASE_LABELS[input.currentPhase];
    }
    return active ? STAGE_DESCRIPTIONS[active] : 'Connecting to AIRA…';
}
/**
 * Turns the event log into a short bullet list of the most recent completed
 * steps. Caps at 4 items to keep the card scannable. Only emits phases that
 * have a human-readable label.
 */
function bulletsFromEvents(events) {
    if (!events?.length)
        return [];
    const seen = new Set();
    const bullets = [];
    for (const ev of [...events].sort((a, b) => b.id - a.id)) {
        const label = PHASE_LABELS[ev.phase];
        if (!label || seen.has(ev.phase))
            continue;
        seen.add(ev.phase);
        const suffix = ev.status === 'completed' ? '✓' : ev.status === 'failed' ? '✗' : '…';
        bullets.push(`${label} ${suffix}`);
        if (bullets.length >= 4)
            break;
    }
    return bullets;
}
export function derivePlanCard(input) {
    const stages = deriveStageStatuses(input.currentPhase, input.status);
    const active = phaseToStage(input.currentPhase);
    return {
        stages,
        activeStage: active,
        title: titleFor(input, active),
        subtitle: subtitleFor(input, active),
        bullets: bulletsFromEvents(input.events),
        badge: confidenceToBadge(input.confidence),
        citationsCount: input.citationsCount ?? 0,
        done: input.status === 'completed',
        failed: input.status === 'failed' || input.status === 'cancelled',
    };
}
/**
 * Convenience — turn the existing flat ProgressStep[] (already used by the
 * Outlook ThinkingPanel) into a `currentPhase` hint for `derivePlanCard`.
 * Picks the latest non-pending step's phase.
 */
export function progressToCurrentPhase(steps) {
    if (!steps?.length)
        return null;
    for (let i = steps.length - 1; i >= 0; i -= 1) {
        if (steps[i].status !== 'pending')
            return steps[i].phase;
    }
    return null;
}
//# sourceMappingURL=stages.js.map